import { apiClient } from './apiClient';
// import { ApiResponse } from './apiClient'; // Unused variable
import { Pagination, PaginatedResponse } from '../types/common';
import { SortDirection } from '@/services/enums';

export interface WatchlistSelfItem {
  id: number;
  symbol: string;
  instType: string;
  createdAt: string;
  key?: string;
  // coin_base 表的字段
  coinBaseSymbol?: string | null;
  imageThumb?: string | null;
  fullyDilutedValuationUsd?: string | null;
  totalVolumeUsd?: string | null;
  totalSupply?: string | null;
  maxSupply?: string | null;
  circulatingSupply?: string | null;
  // 排序字段
  fundingRate?: number | null;
  minFundingRate?: number | null; // 最低资金费率
  circulationRate?: number | null;
  bounce14d?: number | null; // 14天反弹幅度
  bounce30d?: number | null; // 30天反弹幅度
  pullback14d?: number | null; // 14天回调幅度
  pullback30d?: number | null; // 30天回调幅度
  volatility?: number | null; // 波动率（最近4小时15分钟K线波动率中位数）
  cci?: number | null; // CCI指标（1小时K线计算）
  momentumImpulse1h?: number | null; // 脉冲指标（1小时K线计算）
  upProbability?: number | null; // 上涨概率
  downProbability?: number | null; // 下跌概率
}

export interface AddToWatchlistSelfRequest {
  symbol: string;
  instType: string;
  coinId?: string; // CoinGecko coinId，用于生成 coin_base 数据
}

export interface CheckWatchlistSelfResponse {
  isSelfSelected: boolean;
  watchlistId?: number;
}

export interface WatchlistSelfResponse {
  success: boolean; // Keep for interface compatibility if needed, but ApiResponse handles it
  data: WatchlistSelfItem[];
  pagination?: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
}

export class WatchlistSelfService {
  /**
   * 添加币种到自选列表
   */
  async addToWatchlistSelf(symbol: string, instType: string, coinId?: string): Promise<any> {
    const response = await apiClient.post<any>('/watchlist-self/add', {
      symbol,
      instType,
      coinId,
    });

    if (!response.success) {
      throw new Error(response.message || '添加到自选失败');
    }

    return response.data;
  }

  /**
   * 从自选列表中移除币种
   */
  async removeFromWatchlistSelf(symbol: string, instType: string): Promise<any> {
    const response = await apiClient.delete<any>('/watchlist-self/remove', {
      data: {
        symbol,
        instType,
      },
    });

    if (!response.success) {
      throw new Error(response.message || '从自选中移除失败');
    }

    return response.data;
  }

  /**
   * 获取自选列表
   * @param sortBy 排序字段：'default' | 'fundingRate' | 'fullyDilutedValuation' | 'totalVolume' | 'circulationRate' | 'symbol'
   * @param sortOrder 排序方向：SortDirection
   * @param page 页码（从1开始）
   * @param pageSize 每页大小
   * @param search 搜索关键词（币种名称）
   * @param includeExtendedFields 是否计算扩展字段
   */
  async getWatchlistSelf(
    sortBy: string = 'default',
    sortOrder: SortDirection = 'desc',
    page?: number,
    pageSize?: number,
    search?: string,
    includeExtendedFields: boolean = false,
  ): Promise<WatchlistSelfResponse> {
    const params: any = {
      sortBy,
      sortOrder,
      includeExtendedFields: includeExtendedFields ? 'true' : 'false',
    };

    if (page) params.page = page.toString();
    if (pageSize) params.pageSize = pageSize.toString();
    if (search) params.search = search.trim();

    const response = await apiClient.get<WatchlistSelfResponse>('/watchlist-self', {
      params,
    });

    if (!response.success) {
      throw new Error(response.message || '获取自选列表失败');
    }

    return response.data;
  }

  /**
   * 检查是否已自选某币种
   */
  async checkWatchlistStatus(symbol: string, instType: string): Promise<boolean> {
    const response = await apiClient.get<any>('/watchlist-self/check', {
      params: { symbol, instType },
    });

    if (!response.success) {
      throw new Error(response.message || '检查自选状态失败');
    }

    return response.data.isSelected;
  }

  /**
   * 获取可添加的币列表（分页，支持搜索）
   */
  async getAvailableCoins(
    page: number = 1,
    pageSize: number = 10,
    search?: string,
    sortBy: string = 'default',
  ): Promise<PaginatedResponse<any>> {
    const params: any = {
      page,
      pageSize,
      sortBy,
    };
    if (search && search.trim()) {
      params.search = search.trim();
    }

    const response = await apiClient.get<any[]>('/watchlist-self/available', {
      params,
    });

    if (!response.success) {
      throw new Error(response.message || '获取可添加币列表失败');
    }

    return {
      data: response.data || [],
      pagination:
        (response.pagination as Pagination) ||
        ({
          page: 1,
          pageSize: 10,
          total: 0,
          totalPages: 0,
        } as Pagination),
    };
  }

  /**
   * 获取所有不重复的币种列表
   */
  async getDistinctSymbols(): Promise<string[]> {
    try {
      const response = await apiClient.get<string[]>('/watchlist-self/distinct-symbols');

      if (!response.success) {
        throw new Error(response.message || '获取币种列表失败');
      }

      return response.data || [];
    } catch (error: any) {
      console.error('获取币种列表失败:', error);
      throw error;
    }
  }
}

// 创建全局实例
export const watchlistSelfService = new WatchlistSelfService();
